/**
 * Datos demo hardcoded para el tutorial interactivo.
 * Extraidos de los seed files del backend — sin API calls.
 */

export interface TutorialPersona {
  id: string;
  name: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
  roleLabel: string;
  description: string;
  color: string;
  initials: string;
}

export interface TutorialVariable {
  order: number;
  code: string;
  name: string;
  variableType: 'NUMBER' | 'PERCENTAGE' | 'CURRENCY' | 'CALCULATED';
  formula?: string;
  defaultValue?: number;
  isGlobal: boolean;
}

export interface TutorialMaterial {
  code: string;
  name: string;
  externalCode: string;
  description: string;
}

export interface TutorialModel {
  name: string;
  description: string;
  targetMargin: number;
  floorMargin: number;
  variables: TutorialVariable[];
}

// --- Personajes ---

export const PERSONAS: Record<string, TutorialPersona> = {
  carlos: {
    id: 'carlos',
    name: 'Carlos',
    lastName: 'Administrador',
    email: 'admin@acme.com',
    password: 'password123',
    role: 'COMPANY_ADMIN',
    roleLabel: 'Administrador',
    description:
      'Configura modelos de pricing, datos maestros y gestiona el equipo de ACME Corporation.',
    color: 'bg-orange-500',
    initials: 'CA',
  },
  maria: {
    id: 'maria',
    name: 'María',
    lastName: 'Vendedor',
    email: 'maria.vendedor@acme.com',
    password: 'password123',
    role: 'SALESPERSON',
    roleLabel: 'Vendedora',
    description: 'Crea cotizaciones para clientes usando los modelos que Carlos configura.',
    color: 'bg-blue-500',
    initials: 'MV',
  },
  juan: {
    id: 'juan',
    name: 'Juan',
    lastName: 'Aprobador',
    email: 'juan.aprobador@acme.com',
    password: 'password123',
    role: 'APPROVER',
    roleLabel: 'Aprobador',
    description: 'Revisa y aprueba (o rechaza) las cotizaciones que envian los vendedores.',
    color: 'bg-green-500',
    initials: 'JA',
  },
  luna: {
    id: 'luna',
    name: 'Luna',
    lastName: 'Asistente IA',
    email: '',
    password: '',
    role: 'AI_ASSISTANT',
    roleLabel: 'Asistente IA',
    description:
      'Ayuda a todo el equipo: crea cotizaciones, simula precios, genera justificaciones y analiza riesgos.',
    color: 'bg-violet-500',
    initials: 'IA',
  },
};

export const PERSONA_LIST = [PERSONAS.carlos, PERSONAS.maria, PERSONAS.juan, PERSONAS.luna];

// --- Modelo Software (12 variables) ---

export const MODELO_SOFTWARE: TutorialModel = {
  name: 'Modelo Software',
  description: 'Modelo de pricing para licencias de software, implementacion y soporte.',
  targetMargin: 25,
  floorMargin: 15,
  variables: [
    {
      order: 0,
      code: 'CANTIDAD',
      name: 'Cantidad de Licencias',
      variableType: 'NUMBER',
      defaultValue: 1,
      isGlobal: false,
    },
    {
      order: 1,
      code: 'COSTO_LICENCIA',
      name: 'Costo Licencia $us',
      variableType: 'CURRENCY',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 2,
      code: 'DESCUENTO_VOLUMEN',
      name: 'Descuento Volumen %',
      variableType: 'PERCENTAGE',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 3,
      code: 'LICENCIA_CON_DESCUENTO',
      name: 'Licencia con Descuento $us',
      variableType: 'CALCULATED',
      formula: 'COSTO_LICENCIA * (100 - DESCUENTO_VOLUMEN) / 100',
      isGlobal: false,
    },
    {
      order: 4,
      code: 'COSTO_IMPLEMENTACION',
      name: 'Costo Implementacion $us',
      variableType: 'CURRENCY',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 5,
      code: 'COSTO_SOPORTE_ANUAL',
      name: 'Costo Soporte Anual $us',
      variableType: 'CURRENCY',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 6,
      code: 'TOTAL_LICENCIAS',
      name: 'Total Licencias $us',
      variableType: 'CALCULATED',
      formula: 'LICENCIA_CON_DESCUENTO * CANTIDAD',
      isGlobal: false,
    },
    {
      order: 7,
      code: 'SUBTOTAL_SOFTWARE',
      name: 'Subtotal Software $us',
      variableType: 'CALCULATED',
      formula: 'TOTAL_LICENCIAS + COSTO_IMPLEMENTACION + COSTO_SOPORTE_ANUAL',
      isGlobal: false,
    },
    {
      order: 8,
      code: 'MARGEN_PORCENTAJE',
      name: '% Margen',
      variableType: 'PERCENTAGE',
      defaultValue: 25,
      isGlobal: false,
    },
    {
      order: 9,
      code: 'UTILIDAD',
      name: 'Utilidad $us',
      variableType: 'CALCULATED',
      formula: 'SUBTOTAL_SOFTWARE * MARGEN_PORCENTAJE / 100',
      isGlobal: false,
    },
    {
      order: 10,
      code: 'PRECIO_FINAL_UNITARIO',
      name: 'Precio Final Unitario $us',
      variableType: 'CALCULATED',
      formula: '(SUBTOTAL_SOFTWARE + UTILIDAD) / CANTIDAD',
      isGlobal: false,
    },
    {
      order: 11,
      code: 'PRECIO_FINAL_TOTAL',
      name: 'Precio Final Total $us',
      variableType: 'CALCULATED',
      formula: 'SUBTOTAL_SOFTWARE + UTILIDAD',
      isGlobal: false,
    },
  ],
};

export const MODELO_SOPORTE: TutorialModel = {
  name: 'Modelo Soporte',
  description: 'Modelo de pricing para servicios de soporte tecnico por hora.',
  targetMargin: 30,
  floorMargin: 18,
  variables: [
    {
      order: 0,
      code: 'CANTIDAD',
      name: 'Cantidad de Horas',
      variableType: 'NUMBER',
      defaultValue: 1,
      isGlobal: false,
    },
    {
      order: 1,
      code: 'TARIFA_HORA',
      name: 'Tarifa por Hora $us',
      variableType: 'CURRENCY',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 2,
      code: 'COSTO_BASE',
      name: 'Costo Base $us',
      variableType: 'CALCULATED',
      formula: 'CANTIDAD * TARIFA_HORA',
      isGlobal: false,
    },
    {
      order: 3,
      code: 'NIVEL_SOPORTE',
      name: 'Recargo Nivel Soporte %',
      variableType: 'PERCENTAGE',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 4,
      code: 'COSTO_CON_NIVEL',
      name: 'Costo con Nivel $us',
      variableType: 'CALCULATED',
      formula: 'COSTO_BASE * (100 + NIVEL_SOPORTE) / 100',
      isGlobal: false,
    },
    {
      order: 5,
      code: 'DESCUENTO',
      name: 'Descuento %',
      variableType: 'PERCENTAGE',
      defaultValue: 0,
      isGlobal: false,
    },
    {
      order: 6,
      code: 'DESCUENTO_MONTO',
      name: 'Descuento Monto $us',
      variableType: 'CALCULATED',
      formula: 'COSTO_CON_NIVEL * DESCUENTO / 100',
      isGlobal: false,
    },
    {
      order: 7,
      code: 'SUBTOTAL',
      name: 'Subtotal $us',
      variableType: 'CALCULATED',
      formula: 'COSTO_CON_NIVEL - DESCUENTO_MONTO',
      isGlobal: false,
    },
    {
      order: 8,
      code: 'MARGEN_PORCENTAJE',
      name: '% Margen',
      variableType: 'PERCENTAGE',
      defaultValue: 30,
      isGlobal: false,
    },
    {
      order: 9,
      code: 'UTILIDAD',
      name: 'Utilidad $us',
      variableType: 'CALCULATED',
      formula: 'SUBTOTAL * MARGEN_PORCENTAJE / 100',
      isGlobal: false,
    },
    {
      order: 10,
      code: 'PRECIO_FINAL_UNITARIO',
      name: 'Precio Final por Hora $us',
      variableType: 'CALCULATED',
      formula: '(SUBTOTAL + UTILIDAD) / CANTIDAD',
      isGlobal: false,
    },
    {
      order: 11,
      code: 'PRECIO_FINAL_TOTAL',
      name: 'Precio Final Total $us',
      variableType: 'CALCULATED',
      formula: 'SUBTOTAL + UTILIDAD',
      isGlobal: false,
    },
  ],
};

// --- Materiales ---

export const MATERIALS: TutorialMaterial[] = [
  {
    code: 'MAT-SW-001',
    name: 'Microsoft 365 Business Premium',
    externalCode: 'MS-365-BP',
    description: 'Licencia anual por usuario',
  },
  {
    code: 'MAT-SW-002',
    name: 'Adobe Creative Cloud',
    externalCode: 'ADOBE-CC',
    description: 'Licencia anual completa por usuario',
  },
  {
    code: 'MAT-SW-003',
    name: 'VMware vSphere 8 Enterprise Plus',
    externalCode: 'VMWARE-VS8',
    description: 'Licencia por CPU',
  },
  {
    code: 'MAT-SUP-001',
    name: 'Soporte Nivel 1 - Basico',
    externalCode: 'SUP-N1',
    description: 'Mesa de ayuda, incidentes basicos',
  },
  {
    code: 'MAT-SUP-002',
    name: 'Soporte Nivel 2 - Premium',
    externalCode: 'SUP-N2',
    description: 'Especialistas, SLA 4h',
  },
];

// --- Escenario demo: Maria cotiza para Banco Nacional ---

export const DEMO_SCENARIO = {
  client: 'Banco Nacional',
  clientEmail: 'compras@banconacional.com',
  currency: 'USD',
  currencySymbol: '$',
  model: 'Modelo Software',
  material: 'Microsoft 365 Business Premium',
  // Valores iniciales del escenario
  inputs: {
    CANTIDAD: 50,
    COSTO_LICENCIA: 22,
    DESCUENTO_VOLUMEN: 10,
    COSTO_IMPLEMENTACION: 2500,
    COSTO_SOPORTE_ANUAL: 1200,
    MARGEN_PORCENTAJE: 25,
  },
  // Resultados calculados (para referencia)
  calculated: {
    LICENCIA_CON_DESCUENTO: 19.8,
    TOTAL_LICENCIAS: 990,
    SUBTOTAL_SOFTWARE: 4690,
    UTILIDAD: 1172.5,
    PRECIO_FINAL_UNITARIO: 117.25,
    PRECIO_FINAL_TOTAL: 5862.5,
  },
} as const;

// --- LOB hierarchy ---

export const LOB_HIERARCHY = [
  {
    level: 'SECTOR',
    name: 'Tecnologia',
    children: [
      {
        level: 'VERTICAL',
        name: 'Software',
        children: [
          { level: 'PRODUCT', name: 'SaaS' },
          { level: 'PRODUCT', name: 'On-Premise' },
        ],
      },
      {
        level: 'VERTICAL',
        name: 'Hardware',
        children: [
          { level: 'PRODUCT', name: 'Servidores' },
          { level: 'PRODUCT', name: 'Computadores' },
        ],
      },
    ],
  },
];
